import React, { useState, useEffect } from 'react';
import { FaCreditCard, FaEdit, FaTrash, FaMapMarkerAlt, FaUsers, FaCalendarAlt, FaEnvelope, FaGlobe, FaIdCard, FaPhoneAlt, FaChevronDown } from 'react-icons/fa';
import { getImageCacheKey, getImageUrl } from '../../../lib/helpers';

const ShopCard = ({
  id,
  name,
  address,
  city,
  postalCode,
  phone,
  email,
  website,
  taxId,
  description,
  venueType,
  planTier,
  activationStatus,
  subscriptionStatus,
  stripeCustomerId,
  shop_owners,
  date_created,
  handleUpdate,
  handleDelete,
  selectedIds = [],
  onToggleSelect,
  handleManageBilling,
  handleCancelSubscription,
  handleStartBilling,
  handleStartSetup,
  shopImage,
  /** Vista tarjetas: acordeón exclusivo (desde ShopCardGrid) */
  isMobileAccordionOpen,
  onMobileAccordionToggle,
}) => {
  const [imageError, setImageError] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(planTier || 'basico');
  const [internalDetailsOpen, setInternalDetailsOpen] = useState(false);
  const controlledAccordion = typeof onMobileAccordionToggle === 'function';
  const detailsOpen = controlledAccordion ? Boolean(isMobileAccordionOpen) : internalDetailsOpen;
  const venueTypeLabels = {
    cannabis_club: 'Cannabis Club',
    cbd_shop: 'CBD Shop',
    social_club: 'Social Club',
    bistro: 'Bistro',
  };
  
  // Debug: Log owners for this specific card
  useEffect(() => {
    console.log(`[ShopCard] "${name}" (${id}) - Owners received:`, shop_owners);
    if (shop_owners && Array.isArray(shop_owners)) {
      shop_owners.forEach((owner, idx) => {
        console.log(`  [ShopCard] "${name}" - Owner ${idx + 1}:`, {
          id: owner.id || owner,
          email: owner.email,
          name: owner.name,
          fullObject: owner
        });
      });
    }
  }, [name, id, shop_owners]);

  useEffect(() => {
    setSelectedPlan(planTier || 'basico');
  }, [planTier]);
  
  if (typeof handleDelete !== 'function') {
    console.error('handleDelete prop is not a function');
    return null;
  }

  const imageUrl = getImageUrl(shopImage, {
    cacheKey: getImageCacheKey('venue', id),
  });

  // Function to render shop owners
  const renderShopOwners = (owners) => {
    if (!owners || (Array.isArray(owners) && owners.length === 0)) {
      return (
        <span className="text-xs text-gray-400 italic">Sin propietarios asignados</span>
      );
    }
    
    if (Array.isArray(owners)) {
      // Filtrar solo owners válidos (con id y email/name)
      const validOwners = owners.filter(owner => {
        const hasId = owner && (owner.id || (typeof owner === 'string' && owner));
        return hasId;
      });
      
      if (validOwners.length === 0) {
        return <span className="text-xs text-gray-400 italic">Sin propietarios válidos</span>;
      }
      
      return (
        <div className="flex flex-wrap gap-1.5">
          {validOwners.slice(0, 3).map((owner, index) => {
            // Manejar tanto objetos con email/name como UUIDs (para compatibilidad)
            const ownerId = owner.id || owner;
            const ownerName = owner.name || null;
            const ownerEmail = owner.email || null;
            
            // Preferir mostrar el nombre si existe, sino el email, sino un fallback
            const displayText = ownerName || ownerEmail || `Propietario ${index + 1}`;
            
            // Debug log para cada owner renderizado
            console.log(`[ShopCard] "${name}" - Rendering owner ${index + 1}:`, {
              id: ownerId,
              name: ownerName,
              email: ownerEmail,
              displayText
            });
            
            return (
              <span 
                key={`${id}-${ownerId}-${index}`} 
                className="inline-flex items-center gap-1 bg-purple-100 text-purple-700 rounded-full px-2.5 py-1 text-xs font-medium"
                title={ownerEmail && ownerName && ownerName !== ownerEmail ? `${ownerName} (${ownerEmail})` : ownerEmail || ownerName || undefined}
              >
                <FaUsers className="text-[10px]"/>
                {displayText}
              </span>
            );
          })}
          {validOwners.length > 3 && (
            <span className="inline-flex items-center bg-gray-100 text-gray-600 rounded-full px-2.5 py-1 text-xs font-medium">
              +{validOwners.length - 3} más
            </span>
          )}
        </div>
      );
    }
    
    return <span className="text-xs text-gray-400">Sin propietarios asignados</span>;
  };

  // Formatear fecha
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch {
      return dateString;
    }
  };

  const fullAddress = [address, postalCode, city].filter(Boolean).join(', ');
  const planLabels = {
    test: 'Test',
    basico: 'Básico',
    estandar: 'Estándar',
    premium: 'Premium',
  };
  const statusLabels = {
    active: 'Activa',
    trialing: 'Prueba',
    past_due: 'Pago pendiente',
    canceled: 'Cancelada',
    unpaid: 'Impagada',
    incomplete: 'Incompleta',
    incomplete_expired: 'Expirada',
  };
  const planLabel = planLabels[planTier] || 'Básico';
  const subscriptionLabel = statusLabels[subscriptionStatus] || (stripeCustomerId ? 'Pendiente' : 'Sin activar');
  const activationLabel = activationStatus === 'active' ? 'Activa' : 'Pendiente';
  const hasSetupCompleted = activationStatus === 'active';
  const hasSubscription = Boolean(subscriptionStatus) && subscriptionStatus !== 'canceled';
  const nextStepText = !hasSetupCompleted
    ? 'Paso 1. Paga el alta en Stripe. Alli mismo se te pediran los datos de pago.'
    : hasSubscription
      ? 'Suscripcion activa. Si necesitas cambiar tarjeta o datos de cobro, abre el portal de Stripe.'
      : 'Paso 2. Activa la suscripcion mensual en Stripe para dejar la facturacion lista.';
  const selectablePlans = [
    { value: 'test', label: 'Test' },
    { value: 'basico', label: 'Básico' },
    { value: 'estandar', label: 'Estándar' },
    { value: 'premium', label: 'Premium' },
  ];

  return (
    <div className="group flex w-full max-w-full flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm transition-all duration-300 hover:shadow-lg max-lg:h-auto max-lg:self-start lg:h-full">
      {/* Imagen */}
      <div className="relative h-48 sm:h-56 overflow-hidden bg-gradient-to-br from-purple-100 to-purple-200">
        <label className="absolute left-3 top-3 z-20 flex h-6 w-6 items-center justify-center rounded bg-white/95 shadow">
          <input
            type="checkbox"
            checked={selectedIds.some((shopId) => String(shopId) === String(id))}
            onChange={() => onToggleSelect(id)}
            className="h-4 w-4 accent-purple-600"
          />
        </label>
        {!imageError && imageUrl ? (
          <img 
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" 
            src={imageUrl} 
            alt={name || 'Tienda'} 
            onError={() => setImageError(true)}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <div className="text-center">
              <div className="w-20 h-20 mx-auto mb-2 rounded-full bg-purple-500 flex items-center justify-center">
                <span className="text-white text-3xl font-bold">{name?.charAt(0)?.toUpperCase() || 'T'}</span>
              </div>
              <p className="text-purple-600 text-sm font-medium">{name || 'Tienda'}</p>
            </div>
          </div>
        )}
        <div className="absolute top-3 right-3">
          <span className="bg-white/90 backdrop-blur-sm text-purple-700 text-xs font-semibold px-2.5 py-1 rounded-full shadow-sm">
            ID: {id?.toString().slice(0, 8) || '-'}
          </span>
        </div>
      </div>

      {/* Contenido */}
      <div className="flex min-h-0 flex-col p-4 sm:p-5 max-lg:flex-grow-0 lg:flex-grow">
        {/* Móvil/tablet: cabecera con flecha para desplegar detalle */}
        <button
          type="button"
          onClick={() =>
            controlledAccordion ? onMobileAccordionToggle() : setInternalDetailsOpen((v) => !v)
          }
          className="lg:hidden mb-0 flex w-full items-start gap-2 rounded-lg py-1 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-2"
          aria-expanded={detailsOpen}
          aria-controls={`shop-card-details-${id}`}
        >
          <h3 className="line-clamp-2 flex-1 text-xl font-bold text-gray-900 group-hover:text-purple-600 transition-colors sm:text-2xl">
            {name || 'Sin nombre'}
          </h3>
          <FaChevronDown
            className={`mt-1 h-5 w-5 shrink-0 text-purple-600 transition-transform duration-300 ${detailsOpen ? 'rotate-180' : ''}`}
            aria-hidden
          />
        </button>

        <div
          id={`shop-card-details-${id}`}
          className={[
            'flex min-h-0 min-w-0 flex-col overflow-hidden',
            /* Móvil/tablet: sin max-height + flex-grow (dejaba hueco vacío al cerrar). Cerrado = no ocupa espacio */
            detailsOpen ? 'max-lg:flex' : 'max-lg:hidden',
            /* Desktop: siempre visible y participa del alto de la tarjeta */
            'lg:flex lg:flex-grow lg:overflow-visible',
          ].join(' ')}
        >
        {/* Nombre (solo desktop; en móvil va en el botón superior) */}
        <h3 className="mb-2 hidden text-xl font-bold text-gray-900 line-clamp-2 group-hover:text-purple-600 transition-colors sm:text-2xl lg:block">
          {name || 'Sin nombre'}
        </h3>

        <div className="mb-3">
          <span className="inline-flex items-center rounded-full bg-purple-100 px-2.5 py-1 text-xs font-semibold text-purple-700">
            {venueTypeLabels[venueType] || 'Cannabis Club'}
          </span>
        </div>

        {/* Dirección */}
        {fullAddress && (
          <div className="flex items-start gap-2 mb-3">
            <FaMapMarkerAlt className="text-gray-400 text-sm mt-0.5 flex-shrink-0" />
            <p className="text-sm text-gray-600 line-clamp-2 flex-1">{fullAddress}</p>
          </div>
        )}

        {description && (
          <p className="text-sm text-gray-600 mb-3 line-clamp-3">
            {description}
          </p>
        )}

        {(phone || email || website || taxId) && (
          <div className="space-y-2 mb-3">
            {phone && (
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <FaPhoneAlt className="text-gray-400 text-xs" />
                <span>{phone}</span>
              </div>
            )}
            {email && (
              <div className="flex items-center gap-2 text-sm text-gray-600 break-all">
                <FaEnvelope className="text-gray-400 text-xs" />
                <span>{email}</span>
              </div>
            )}
            {website && (
              <div className="flex items-center gap-2 text-sm text-gray-600 break-all">
                <FaGlobe className="text-gray-400 text-xs" />
                <span>{website}</span>
              </div>
            )}
            {taxId && (
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <FaIdCard className="text-gray-400 text-xs" />
                <span>CIF/NIF: {taxId}</span>
              </div>
            )}
          </div>
        )}

        <div className="mb-3 rounded-lg border border-gray-100 bg-gray-50 p-3">
          <div className="flex items-center gap-1.5 mb-2">
            <FaCreditCard className="text-gray-400 text-xs" />
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Facturación</span>
          </div>
          <div className="flex flex-wrap gap-2 mb-2">
            <span className="inline-flex items-center rounded-full bg-purple-100 px-2.5 py-1 text-xs font-medium text-purple-700">
              Plan {planLabel}
            </span>
            <span className="inline-flex items-center rounded-full bg-gray-200 px-2.5 py-1 text-xs font-medium text-gray-700">
              Alta: {activationLabel}
            </span>
            <span className="inline-flex items-center rounded-full bg-gray-200 px-2.5 py-1 text-xs font-medium text-gray-700">
              Suscripción: {subscriptionLabel}
            </span>
          </div>
          <div className="mb-2">
            <label className="mb-1 block text-xs font-medium text-gray-600">Plan para activar</label>
            <select
              value={selectedPlan}
              onChange={(e) => setSelectedPlan(e.target.value)}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700"
            >
              {selectablePlans.map((plan) => (
                <option key={plan.value} value={plan.value}>
                  {plan.label}
                </option>
              ))}
            </select>
          </div>
          <div className="mb-3 rounded-lg bg-white px-3 py-2 text-xs leading-5 text-gray-600 border border-gray-200">
            {nextStepText}
          </div>
          {!hasSetupCompleted ? (
            <button
              type="button"
              onClick={() => handleStartSetup?.(id, selectedPlan)}
              className="w-full rounded-lg bg-purple-600 px-3 py-2 text-sm font-medium text-white hover:bg-purple-700"
            >
              Paso 1: pagar alta en Stripe
            </button>
          ) : hasSubscription ? (
            <div className="grid grid-cols-1 gap-2">
              <button
                type="button"
                onClick={() => handleManageBilling?.(id)}
                className="w-full rounded-lg border border-purple-300 bg-white px-3 py-2 text-sm font-medium text-purple-700 hover:bg-purple-50"
              >
                Actualizar tarjeta y facturacion
              </button>
              <button
                type="button"
                onClick={() => handleCancelSubscription?.(id)}
                className="w-full rounded-lg border border-red-300 bg-white px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50"
              >
                Cancelar suscripción
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => handleStartBilling?.(id, selectedPlan)}
              className="w-full rounded-lg bg-purple-600 px-3 py-2 text-sm font-medium text-white hover:bg-purple-700"
            >
              Paso 2: activar suscripcion
            </button>
          )}
        </div>

        {/* Propietarios */}
        <div className="mb-3">
          <div className="flex items-center gap-1.5 mb-1.5">
            <FaUsers className="text-gray-400 text-xs" />
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Propietarios</span>
          </div>
          {renderShopOwners(shop_owners)}
        </div>

        {/* Fecha de creación */}
        {date_created && (
          <div className="flex items-center gap-2 mt-auto pt-3 border-t border-gray-100">
            <FaCalendarAlt className="text-gray-400 text-xs" />
            <span className="text-xs text-gray-500">
              Creada: <span className="font-medium">{formatDate(date_created)}</span>
            </span>
          </div>
        )}
        </div>
      </div>

      {/* Botones de acción */}
      <div className="px-4 sm:px-5 pb-4 sm:pb-5 pt-2 border-t border-gray-100">
        <div className="grid grid-cols-2 gap-2 sm:gap-3">
          <button 
            className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold shadow-sm hover:shadow transition-all active:scale-95"
            onClick={() => handleUpdate(id)}
          >
            <FaEdit className="text-sm"/>
            <span>Editar</span>
          </button>
          <button 
            className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-semibold shadow-sm hover:shadow transition-all active:scale-95"
            onClick={(event) => {
              event.stopPropagation();
              handleDelete(id);
            }}
          >
            <FaTrash className="text-sm"/>
            <span>Eliminar</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ShopCard;
